import { getVueCompositionApi } from "../utils/vue-compat";
import { KpiData } from "../types";

// Import Vue types dynamically
type Ref<T> = any;
type ComputedRef<T> = any;

export interface UseKpiTrackerReturn {
  // State
  confirmedConsent: Ref<boolean>;
  enteredCrmData: Ref<boolean>;
  gamePlayTimeStart: Ref<number | null>;
  gamePlayTimeSeconds: Ref<number>;
  voucherRedeemed: Ref<boolean>;
  timesPlayed: Ref<number>;
  selectedPerson: Ref<number>;
  timesLeftToPlay: Ref<number>;
  progress: Ref<number>;

  // Actions
  setConfirmedConsent: (value: boolean) => void;
  setEnteredCrmData: (value: boolean) => void;
  setGamePlayTimeStart: (timestamp: number | null) => void;
  setGamePlayTimeSeconds: (seconds: number) => void;
  setVoucherRedeemed: (value: boolean) => void;
  incrementTimesPlayed: () => void;
  setSelectedPerson: (person: number) => void;
  setProgress: (value: number) => void;
  resetTrackingData: () => void;
  resetGame: (timesLeftToPlay?: number) => void;

  // Computed
  getKpiData: () => KpiData;
}

export function useKpiTracker(
  initialTimesLeftToPlay: number = 2
): UseKpiTrackerReturn {
  const vue = getVueCompositionApi();
  const { ref } = vue;

  // State - TypeScript kann die Typen aus dem Wert inferieren
  const confirmedConsent = ref(false) as any;
  const enteredCrmData = ref(false) as any;
  const gamePlayTimeStart = ref(null as number | null) as any;
  const gamePlayTimeSeconds = ref(0) as any;
  const voucherRedeemed = ref(false) as any;
  const timesPlayed = ref(0) as any;
  const selectedPerson = ref(1) as any;
  const timesLeftToPlay = ref(initialTimesLeftToPlay) as any;
  const progress = ref(0) as any;

  // Actions
  const setConfirmedConsent = (value: boolean) => {
    confirmedConsent.value = value;
  };

  const setEnteredCrmData = (value: boolean) => {
    enteredCrmData.value = value;
  };

  const setGamePlayTimeStart = (timestamp: number | null) => {
    gamePlayTimeStart.value = timestamp;
  };

  const setGamePlayTimeSeconds = (seconds: number) => {
    gamePlayTimeSeconds.value = seconds;
  };

  const setVoucherRedeemed = (value: boolean) => {
    voucherRedeemed.value = value;
  };

  const incrementTimesPlayed = () => {
    if (timesLeftToPlay.value > 0) {
      timesPlayed.value++;
      timesLeftToPlay.value--;
    }
  };

  const setSelectedPerson = (person: number) => {
    selectedPerson.value = person;
    // Only reset game play time if it hasn't been started yet
    // Don't reset confirmed_consent or entered_crm_data
    const wasGamePlayTimeStarted = gamePlayTimeStart.value !== null;
    timesPlayed.value = 0;
    timesLeftToPlay.value = person * 2;
    // Only reset game play time if it wasn't started yet
    if (!wasGamePlayTimeStarted) {
      gamePlayTimeStart.value = null;
    }
    gamePlayTimeSeconds.value = 0;
    voucherRedeemed.value = false;
  };

  const setProgress = (value: number) => {
    // Ensure progress is always between 0 and 3 (valid steps)
    // 0: opened_game, 1: age_gate_clicked, 2: game_started, 3: game_finished
    if (value < 0 || value > 3) {
      console.warn(`[KpiTracker] Invalid progress value: ${value}. Must be between 0 and 3. Clamping to valid range.`);
      progress.value = Math.max(0, Math.min(3, value));
    } else {
      progress.value = value;
    }
  };

  const resetTrackingData = () => {
    confirmedConsent.value = false;
    enteredCrmData.value = false;
    gamePlayTimeStart.value = null;
    gamePlayTimeSeconds.value = 0;
    voucherRedeemed.value = false;
    progress.value = 0;
  };

  const resetGame = (newTimesLeftToPlay?: number) => {
    timesPlayed.value = 0;
    timesLeftToPlay.value = newTimesLeftToPlay ?? initialTimesLeftToPlay;
    gamePlayTimeStart.value = null;
    gamePlayTimeSeconds.value = 0;
    voucherRedeemed.value = false;
  };

  const getKpiData = (): KpiData => {
    return {
      times_played: timesPlayed.value,
      selected_person: selectedPerson.value,
      confirmed_consent: confirmedConsent.value,
      entered_crm_data: enteredCrmData.value,
      game_play_time_seconds: gamePlayTimeSeconds.value,
      voucher_redeemed: voucherRedeemed.value,
      progress: progress.value,
    };
  };

  return {
    // State
    confirmedConsent,
    enteredCrmData,
    gamePlayTimeStart,
    gamePlayTimeSeconds,
    voucherRedeemed,
    timesPlayed,
    selectedPerson,
    timesLeftToPlay,
    progress,

    // Actions
    setConfirmedConsent,
    setEnteredCrmData,
    setGamePlayTimeStart,
    setGamePlayTimeSeconds,
    setVoucherRedeemed,
    incrementTimesPlayed,
    setSelectedPerson,
    setProgress,
    resetTrackingData,
    resetGame,

    // Computed
    getKpiData,
  };
}
